import { EnumCellEditor } from './EnumCellEditor';
import { FkCellEditor, type FkPickerTarget } from './FkCellEditor';
import { BooleanCellEditor } from './BooleanCellEditor';
import type { CellEditVariantResult } from './cellEditVariant';
import type { TableExplorerRelationshipPort } from '../../../table-explorer/components/tableExplorerHostPorts';

export type SmartCellInputProps = {
  variant: CellEditVariantResult;
  initialValue: unknown;
  /** Connection + database the column lives in (required for the enum/FK pickers). */
  connectionId: string;
  database: string;
  /** Column coordinates (engine + schema + table + name). */
  engine: string;
  schema: string;
  table: string;
  column: string;
  /** Source column's dataType — used by FkLookupPicker's target-selection UI. */
  dataType?: string;
  /** Source signal for the enum branch: 'declared' or 'native'. */
  enumSource?: 'declared' | 'native';
  onCommit: (value: unknown) => void;
  onCancel: () => void;
  /** Host relationship / metadata port (Azure Table): the pickers read through it, never SQL (S4 review C, M7). */
  relationshipPort?: TableExplorerRelationshipPort;
  /** The host refused the last commit (S4 review G, G5): the input is marked `aria-invalid`. SQL never sets it. */
  invalid?: boolean;
  /** Caller passes tailwind classes so we match the host grid's chrome. */
  inputClassName?: string;
  containerClassName?: string;
};

/**
 * Renders an enum-aware or FK-aware cell editor. Returns `null` when variant
 * is 'plain' — caller must keep their existing type-aware input path.
 */
export function SmartCellInput(props: SmartCellInputProps) {
  if (props.variant.variant === 'enum') {
    return (
      <EnumCellEditor
        initialValue={props.initialValue}
        connectionId={props.connectionId}
        database={props.database}
        schema={props.schema}
        table={props.table}
        column={props.column}
        engine={props.engine}
        source={props.enumSource ?? 'declared'}
        nullable={props.variant.nullable ?? true}
        // Present when the declared enum outranked an FK on this column —
        // the editor turns them into its "Search rows in …" footer escape.
        fkTarget={props.variant.fkTarget}
        looseMatches={props.variant.looseMatches}
        dataType={props.dataType}
        relationshipPort={props.relationshipPort}
        invalid={props.invalid}
        onCommit={props.onCommit}
        onCancel={props.onCancel}
        inputClassName={props.inputClassName}
        containerClassName={props.containerClassName}
      />
    );
  }

  if (props.variant.variant === 'boolean') {
    return (
      <BooleanCellEditor
        initialValue={props.initialValue}
        nullable={props.variant.nullable}
        onCommit={props.onCommit}
        onCancel={props.onCancel}
        inputClassName={props.inputClassName}
        containerClassName={props.containerClassName}
      />
    );
  }

  if (props.variant.variant === 'fk') {
    const targets: FkPickerTarget[] = props.variant.looseMatches && props.variant.looseMatches.length > 0
      ? props.variant.looseMatches.map((rel) => ({
          schema: rel.toSchema,
          table: rel.toTable,
          column: rel.toColumn,
          isLoose: true,
          looseRel: rel,
        }))
      : props.variant.fkTarget
        ? [
            {
              schema: props.variant.fkTarget.schema,
              table: props.variant.fkTarget.table,
              column: props.variant.fkTarget.column,
              isLoose: props.variant.fkTarget.isLoose,
            },
          ]
        : [];
    if (targets.length === 0) return null;
    return (
      <FkCellEditor
        initialValue={props.initialValue}
        connectionId={props.connectionId}
        database={props.database}
        engine={props.engine}
        sourceSchema={props.schema}
        sourceTable={props.table}
        sourceColumn={props.column}
        sourceDataType={props.dataType}
        targets={targets}
        relationshipPort={props.relationshipPort}
        invalid={props.invalid}
        onCommit={props.onCommit}
        onCancel={props.onCancel}
        inputClassName={props.inputClassName}
        containerClassName={props.containerClassName}
      />
    );
  }

  return null;
}
